import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

let supabaseClient;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ MISSING SUPABASE ENVIRONMENT VARIABLES');
  console.error('Please create a .env file with:');
  console.error('REACT_APP_SUPABASE_URL=your_supabase_url');
  console.error('REACT_APP_SUPABASE_ANON_KEY=your_supabase_key');

  // Create a dummy client to prevent crashes
  supabaseClient = {
    auth: {
      signUp: () => Promise.reject(new Error('Supabase not configured')),
      signInWithPassword: () => Promise.reject(new Error('Supabase not configured')),
      signInWithOAuth: () => Promise.reject(new Error('Supabase not configured')),
      signOut: () => Promise.reject(new Error('Supabase not configured')),
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    },
    from: () => ({
      select: () => Promise.reject(new Error('Supabase not configured')),
      insert: () => Promise.reject(new Error('Supabase not configured')),
    }),
  };
} else {
  // console.log('✅ Supabase configuration found');
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: window?.localStorage,
    },
  });
}

export const supabase = supabaseClient;

// Dedicated client for Study Materials & Current Affairs database
const studyUrl =
  process.env.REACT_APP_SUPABASE_STUDY_URL ||
  process.env.SUPABASE_STUDY_URL ||
  'https://zviqdvaiolqrlknybylp.supabase.co';
const studyKey =
  process.env.REACT_APP_SUPABASE_STUDY_ANON_KEY ||
  process.env.REACT_APP_SUPABASE_STUDY_KEY ||
  process.env.SUPABASE_STUDY_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp2aXFkdmFpb2xxcmxrbnlieWxwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODY1MzEwNjAsImV4cCI6MjEwMjEwNzA2MH0.Sjix7dVFKXg7GQnq_oAAY2HVwnKzZilRjHsRafZfH7Y';

export const supabaseStudy =
  studyUrl && studyKey
    ? createClient(studyUrl, studyKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      })
    : supabaseClient;

// Login with Google
export const loginWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      console.error('Google login error:', error);
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Google login function error:', error);
    throw error;
  }
};

// Sign out
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

// Get current user
export const getCurrentUser = () => {
  return supabase.auth.getUser();
};

// Get categories
export const getCategories = async () => {
  try {
    const { data, error } = await supabase.from('exam_categories').select('*').order('name');

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Get categories error:', error);
    throw error;
  }
};

// Get exams
export const getExams = async () => {
  try {
    const { data, error } = await supabase.from('exams').select('*').order('name');

    if (error) {
      console.error('Error fetching exams:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Get exams error:', error);
    throw error;
  }
};

// Exam related functions with error handling
export const fetchExams = async () => {
  const { data, error } = await supabase
    .from('exams')
    .select('id, name, description, category_id')
    .order('name');

  if (error) {
    console.error('Error fetching exams:', error);
    throw new Error('Failed to load exams. Please try again.');
  }

  return data || [];
};

export const fetchCategories = async () => {
  const { data, error } = await supabase.from('exam_categories').select('*').order('name');

  if (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Failed to load exam categories. Please try again.');
  }

  return data || [];
};

export const fetchExamsByCategory = async (categoryId) => {
  const { data, error } = await supabase
    .from('exams')
    .select('id, name, description, category_id')
    .eq('category_id', categoryId)
    .order('name');

  if (error) {
    console.error('Error fetching exams by category:', error);
    throw new Error('Failed to load exams. Please try again.');
  }

  return data || [];
};

// User profile functions
export const createUserProfile = async (userId, profileData) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .upsert(
      [
        {
          user_id: userId,
          email: profileData.email || '',
          full_name: profileData.full_name || '',
          phone_number: profileData.phone_number || '',
          state: profileData.state || null,
          selected_exams: profileData.selected_exams || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ],
      { onConflict: 'user_id' }
    )
    .select()
    .single();

  if (error && error.code !== '23505') {
    // Ignore duplicate key error
    console.error('Error creating user profile:', error);
    throw new Error('Failed to create user profile.');
  }

  return data;
};

export const updateUserProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating user profile:', error);
    throw new Error('Failed to update user profile.');
  }

  return data;
};

// Intercept study material and current affairs queries to redirect to dedicated study database
if (supabase && typeof supabase.from === 'function') {
  const originalFrom = supabase.from.bind(supabase);
  supabase.from = function (table) {
    if (
      table === 'study_materials' ||
      table === 'study_material_sections' ||
      table === 'current_affairs_articles' ||
      table === 'current_affairs_ratings'
    ) {
      return supabaseStudy.from(table);
    }
    return originalFrom(table);
  };
}
